import { Form, Formik, Field, ErrorMessage } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import * as Yup from "yup";
import { getNewLeadAsync, updateNewLeadAsync, checkNewLeadContactNumberExistsAsync } from "../redux";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { regexValidation } from "../../../../_metronic/_helpers";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";

const normalizeCompanySelectValue = (v) => {
  if (v == null || v === "") return "webclues";
  const s = String(v).toLowerCase();
  if (s.includes("codezero")) return "codezeros";
  return "webclues";
};

const ViewNewLead = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { newLeadDetails } = useSelector(
    (state) => ({
      newLeadDetails: state.newDataCall.selectedColdLead,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (id) {
      dispatch(getNewLeadAsync(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const redirectBack = () => {
    history.push("/new-data-call");
  };

  const validationSchema = Yup.object().shape({
    companyName: Yup.string()
      .oneOf(["webclues", "codezeros"], "Select Webclues or Codezeros")
      .required("Company name is required"),
    clientName: Yup.string()
      .trim()
      .required("Client name is required")
      .min(1, "Client name is required")
      .max(200, "Client name must not exceed 200 characters"),
    clientCompanyName: Yup.string()
      .trim()
      .nullable()
      .notRequired()
      .max(200, "Client company name must not exceed 200 characters"),
    email: Yup.string()
      .trim()
      .nullable()
      .notRequired()
      .test("email-format", "Enter a valid email", (value) => {
        if (!value || value.length === 0) return true;
        return regexValidation.EMAIL_REGEX.test(value);
      }),
    contactNumber: Yup.string()
      .trim()
      .required("Contact number is required")
      .matches(regexValidation.CONTACT_NUMBER_REGEX, "Contact number is not valid"),
    industry: Yup.string()
      .trim()
      .required("Industry is required")
      .min(1, "Industry is required")
      .max(200, "Industry must not exceed 200 characters"),
  });

  const initialValues = {
    companyName: normalizeCompanySelectValue(newLeadDetails?.CompanyName),
    clientName: newLeadDetails?.ClientName || "",
    clientCompanyName: newLeadDetails?.ClientCompanyName || "",
    email: newLeadDetails?.Email || newLeadDetails?.email || "",
    contactNumber: newLeadDetails?.ContactNumber || newLeadDetails?.contactNumber || "",
    industry: newLeadDetails?.Industry || newLeadDetails?.Industrial || "",
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setIsSubmitting(true);
    try {
      const trimmedContact = values.contactNumber?.trim() || "";
      const originalContact = (
        newLeadDetails?.ContactNumber ||
        newLeadDetails?.contactNumber ||
        ""
      ).trim();

      if (trimmedContact !== originalContact) {
        const duplicateCheck = await dispatch(
          checkNewLeadContactNumberExistsAsync(trimmedContact, id)
        );
        if (duplicateCheck && duplicateCheck.exists === true) {
          setFieldError("contactNumber", "This number already exists");
          setIsSubmitting(false);
          setSubmitting(false);
          return;
        }
      }

      const capitalizeWords = (str) =>
        str ? str.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") : "";

      const rawCompany = (values.companyName || "").toLowerCase();
      const capitalizedCompany =
        rawCompany === "codezeros" ? "Codezeros" : "Webclues";

      const submitData = {
        CompanyName: capitalizedCompany,
        ClientName: capitalizeWords((values.clientName || "").trim()),
        ClientCompanyName: capitalizeWords((values.clientCompanyName || "").trim()),
        Industry: (values.industry || "").trim(),
        ContactNumber: trimmedContact,
        Email: (values.email || "").trim(),
      };

      const result = await dispatch(updateNewLeadAsync(id, submitData));

      if (result && result.success) {
        setTimeout(() => {
          redirectBack();
        }, 1000);
      } else {
        if (result && result.isDuplicate) {
          setFieldError("contactNumber", "This number already exists");
        }
      }
    } catch (error) {
      console.error("Exception during update:", error);
      dispatch(showSuccessSnackbar("error", "Error while updating new data call", 3000));
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const getInputClasses = (fieldname, touched, errors) => {
    if (touched && errors) {
      return "form-control re_inputRouded is-invalid";
    }
    if (touched && !errors) {
      return "form-control re_inputRouded is-valid";
    }
    return "form-control re_inputRouded";
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ touched, errors, isSubmitting: formikSubmitting }) => (
        <Row className="justify-content-center">
          <Col lg={11}>
            <Card>
              <CardHeader title="Edit New Data Call" />
              <CardBody>
                <Form>
                  <div className="form-group">
                    <label className="form-label">Company Name</label>
                    <Field
                      as="select"
                      className={getInputClasses("companyName", touched.companyName, errors.companyName)}
                      name="companyName"
                    >
                      <option value="webclues">Webclues</option>
                      <option value="codezeros">Codezeros</option>
                    </Field>
                    <ErrorMessage
                      name="companyName"
                      component="div"
                      className="invalid-feedback d-block"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Client Name</label>
                    <Field
                      className={getInputClasses("clientName", touched.clientName, errors.clientName)}
                      placeholder="Client Name"
                      type="text"
                      name="clientName"
                    />
                    <ErrorMessage name="clientName" component="div" className="invalid-feedback" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Client Company Name</label>
                    <Field
                      className={getInputClasses(
                        "clientCompanyName",
                        touched.clientCompanyName,
                        errors.clientCompanyName
                      )}
                      placeholder="Client Company Name"
                      type="text"
                      name="clientCompanyName"
                    />
                    <ErrorMessage
                      name="clientCompanyName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <Field
                      className={getInputClasses("email", touched.email, errors.email)}
                      placeholder="Email"
                      type="email"
                      name="email"
                    />
                    <ErrorMessage name="email" component="div" className="invalid-feedback" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact Number</label>
                    <Field
                      className={getInputClasses(
                        "contactNumber",
                        touched.contactNumber,
                        errors.contactNumber
                      )}
                      placeholder="Contact Number"
                      type="text"
                      name="contactNumber"
                    />
                    <ErrorMessage
                      name="contactNumber"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Industry</label>
                    <Field
                      className={getInputClasses("industry", touched.industry, errors.industry)}
                      placeholder="Industry"
                      type="text"
                      name="industry"
                    />
                    <ErrorMessage name="industry" component="div" className="invalid-feedback" />
                  </div>
                  <div className="text-center">
                    <Button
                      variant="blue"
                      className="px-5 defpddng me-3 mr-3"
                      size="lg"
                      type="submit"
                      disabled={isSubmitting || formikSubmitting}
                    >
                      {isSubmitting || formikSubmitting ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      variant="secondary"
                      className="px-5 defpddng"
                      size="lg"
                      type="button"
                      onClick={redirectBack}
                      disabled={isSubmitting || formikSubmitting}
                    >
                      Close
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Formik>
  );
};

export default ViewNewLead;
